import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { FieldOverlay, SchemaOverlay } from '../components/AdminConfig/configSchema';

/**
 * Client for `getAiScoringPenaltyDefaults` (watchlock, super-admin, read-only)
 * and the translation of its payload into the field overlay that
 * `SchemaConfigPanel` renders the AI Scoring panels with.
 *
 * The callable exists so this app never restates a number watchlock owns — the
 * shipped magnitudes, which slot honours which field, and the permitted range
 * all move when scoring is retuned, and a hardcoded copy here would drift
 * silently. So every number these panels display is fetched, and when the fetch
 * fails we render nothing rather than a stale guess (see `penaltyFieldOverlay`).
 *
 * It serves DEFAULTS only, never the live config: the panels read the live RTDB
 * node directly, exactly like every other `configuration/*` screen. Keeping the
 * two apart is what stops the editor writing today's defaults back as explicit
 * overrides and pinning them forever.
 */

/** One slot, in the sign convention the RTDB node uses (negative). */
export interface PenaltySlotDefaults {
  slot: string;
  /** Magnitude keys THIS slot honours — omits `nearPenalty` where it would be inert. */
  fields: string[];
  defaults: {
    mismatchPenalty: number;
    /** null when the slot's classifier can never return `near`. */
    nearPenalty: number | null;
    nullPenalty: number;
  };
  /** Gate state applied when the RTDB key is absent. */
  defaultFlags: { mismatch: boolean; null: boolean };
  /** True only where the mismatch magnitude is a ramped ceiling, not a flat charge. */
  scaled: boolean;
}

export interface AiScoringPenaltyDefaults {
  configPath: string;
  /** Permitted range for every magnitude field. */
  range: { min: number; max: number };
  /** Total penalty ceiling across all slots. Not configurable. */
  cap: number;
  /** Extraction confidence below which no slot penalises at all. */
  minConfidence: number;
  /** Catalogue coverage at which enabling a slot's `null` gate becomes safe. */
  nullFlagCoverageTarget: number;
  slots: PenaltySlotDefaults[];
}

/**
 * In-flight/settled fetch, shared by the five panels so one screen load makes
 * one call. A REJECTED promise is dropped rather than cached: the callable is
 * genuinely unavailable before it has been deployed, and after a deploy the
 * next panel mount must be able to succeed without a page reload.
 */
let pending: Promise<AiScoringPenaltyDefaults> | null = null;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Validate the payload before any of it reaches the UI. This is our own
 * backend, but a partial deploy can serve an older shape, and a silently
 * missing field would surface as a placeholder reading "default undefined".
 * Anything malformed is treated as unavailable — the panels then hide the
 * magnitude fields entirely rather than describe them wrongly.
 */
function normalise(raw: unknown): AiScoringPenaltyDefaults {
  const o = (raw ?? {}) as Record<string, unknown>;
  const range = (o.range ?? {}) as Record<string, unknown>;
  if (!isFiniteNumber(range.min) || !isFiniteNumber(range.max)) {
    throw new Error('malformed payload: range');
  }
  if (!isFiniteNumber(o.cap) || !isFiniteNumber(o.minConfidence)) {
    throw new Error('malformed payload: cap/minConfidence');
  }
  if (!isFiniteNumber(o.nullFlagCoverageTarget)) {
    throw new Error('malformed payload: nullFlagCoverageTarget');
  }
  if (!Array.isArray(o.slots) || o.slots.length === 0) {
    throw new Error('malformed payload: slots');
  }

  const slots: PenaltySlotDefaults[] = o.slots.map((entry, i) => {
    const s = (entry ?? {}) as Record<string, unknown>;
    const defaults = (s.defaults ?? {}) as Record<string, unknown>;
    const flags = (s.defaultFlags ?? {}) as Record<string, unknown>;
    if (typeof s.slot !== 'string' || !s.slot) {
      throw new Error(`malformed payload: slots[${i}].slot`);
    }
    if (!Array.isArray(s.fields)) {
      throw new Error(`malformed payload: slots[${i}].fields`);
    }
    if (!isFiniteNumber(defaults.mismatchPenalty) || !isFiniteNumber(defaults.nullPenalty)) {
      throw new Error(`malformed payload: slots[${i}].defaults`);
    }
    // `nearPenalty` is legitimately null on the four slots that fold same-family
    // into `match`; anything other than a number or null is malformed.
    if (defaults.nearPenalty !== null && !isFiniteNumber(defaults.nearPenalty)) {
      throw new Error(`malformed payload: slots[${i}].defaults.nearPenalty`);
    }
    if (typeof flags.mismatch !== 'boolean' || typeof flags.null !== 'boolean') {
      throw new Error(`malformed payload: slots[${i}].defaultFlags`);
    }
    return {
      slot: s.slot,
      fields: s.fields.filter((f): f is string => typeof f === 'string'),
      defaults: {
        mismatchPenalty: defaults.mismatchPenalty,
        nearPenalty: defaults.nearPenalty as number | null,
        nullPenalty: defaults.nullPenalty,
      },
      defaultFlags: { mismatch: flags.mismatch, null: flags.null },
      scaled: s.scaled === true,
    };
  });

  return {
    configPath: typeof o.configPath === 'string' ? o.configPath : '',
    range: { min: range.min, max: range.max },
    cap: o.cap,
    minConfidence: o.minConfidence,
    nullFlagCoverageTarget: o.nullFlagCoverageTarget,
    slots,
  };
}

export function fetchAiScoringPenaltyDefaults(): Promise<AiScoringPenaltyDefaults> {
  if (pending) return pending;
  const fn = httpsCallable<void, unknown>(functions, 'getAiScoringPenaltyDefaults');
  pending = fn()
    .then((res) => normalise(res.data))
    .catch((err: unknown) => {
      pending = null; // never cache a failure — see `pending`
      throw err;
    });
  return pending;
}

// ── Field overlay ────────────────────────────────────────────────────────────

/** Penalties read as negatives with a consistent 2dp, so a column of them lines up. */
function fmtPenalty(n: number): string {
  return n.toFixed(2);
}

function onOff(v: boolean): string {
  return v ? 'on' : 'off';
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'unknown error';
}

/**
 * Build the runtime field metadata for one slot's panel.
 *
 * Everything numeric here comes off the payload. The `hidden` flags come from
 * the slot's own `fields` list rather than a local table, so if watchlock ever
 * teaches another slot to return `near`, that slot's panel grows the field with
 * no change here.
 */
export async function penaltyFieldOverlay(slot: string): Promise<SchemaOverlay> {
  let payload: AiScoringPenaltyDefaults;
  try {
    payload = await fetchAiScoringPenaltyDefaults();
  } catch (err) {
    return { status: 'unavailable', reason: errorMessage(err) };
  }

  const meta = payload.slots.find((s) => s.slot === slot);
  if (!meta) {
    return { status: 'unavailable', reason: `the backend does not list a "${slot}" slot` };
  }

  const { min, max } = payload.range;
  const coveragePct = Math.round(payload.nullFlagCoverageTarget * 100);
  const capNote = `Range ${fmtPenalty(min)} to ${fmtPenalty(max)}; the total across all slots is capped at ${fmtPenalty(payload.cap)}.`;
  const emptyNote = 'Leave empty to use the shipped default.';

  const magnitude = (
    key: 'mismatchPenalty' | 'nearPenalty' | 'nullPenalty',
    fallback: number | null,
    extra?: string,
  ): FieldOverlay => ({
    hidden: !meta.fields.includes(key) || fallback === null,
    placeholder: fallback === null ? undefined : `default ${fmtPenalty(fallback)}`,
    min,
    max,
    helpSuffix: [emptyNote, extra, capNote].filter(Boolean).join(' '),
  });

  return {
    status: 'ready',
    fields: {
      mismatch: {
        defaultValue: meta.defaultFlags.mismatch,
        helpSuffix: `Shipped default: ${onOff(meta.defaultFlags.mismatch)}. Nothing is penalised at all when extraction confidence is below ${payload.minConfidence}.`,
      },
      null: {
        defaultValue: meta.defaultFlags.null,
        helpSuffix: `Shipped default: ${onOff(meta.defaultFlags.null)}. Only safe once this slot's catalogue coverage clears ${coveragePct}%, below which it shifts every candidate down equally instead of reordering them.`,
      },
      mismatchPenalty: magnitude(
        'mismatchPenalty',
        meta.defaults.mismatchPenalty,
        meta.scaled
          ? 'This slot charges a RAMPED penalty: the value here is the ceiling reached at a large discrepancy, not a flat charge — smaller errors are charged proportionally less.'
          : undefined,
      ),
      nearPenalty: magnitude('nearPenalty', meta.defaults.nearPenalty),
      nullPenalty: magnitude('nullPenalty', meta.defaults.nullPenalty),
    },
  };
}
